/**
 * Remembers which join requests a supplier has already been greeted about, so
 * the "you got a request to join" popup jumps at them once per request and
 * not on every visit to My Communities.
 *
 * A request is identified by its community *and* the time it was made: a
 * community that asks again after being ignored is a new request and gets its
 * own greeting, which keying on the community alone would have swallowed
 * forever.
 *
 * Kept per supplier id: the same browser is regularly shared between accounts
 * in dev, and one account's greeting must not silence another's.
 */
const STORAGE_KEY_PREFIX = "greeted-join-requests";

// Only the recent tail is worth keeping — older requests are long since
// answered, and the list should not grow without end.
const MAX_REMEMBERED = 50;

const storageKey = (supplierId) => `${STORAGE_KEY_PREFIX}:${supplierId}`;

/**
 * The identity of a single join request, from a row of the join-requests list.
 *
 * The list has no request id, so the community plus the moment it asked stands
 * in for one: a community that withdraws and asks again is a new request and
 * greets again. `creation_time` is the full timestamp; `request_date` is the
 * same instant at day precision and only serves an API that predates it.
 */
export const getJoinRequestKey = (request) => {
  if (!request?.community_id) return null;
  const askedAt = request.creation_time ?? request.request_date ?? "";
  return [request.community_id, askedAt].map(String).join("|");
};

export const getGreetedJoinRequestKeys = (supplierId) => {
  if (!supplierId) return [];
  try {
    const stored = JSON.parse(localStorage.getItem(storageKey(supplierId)));
    return Array.isArray(stored) ? stored : [];
  } catch {
    // A malformed entry is treated as "nothing greeted yet" rather than
    // breaking the page.
    return [];
  }
};

export const markJoinRequestGreeted = (supplierId, requestKey) => {
  if (!supplierId || !requestKey) return;
  const greeted = getGreetedJoinRequestKeys(supplierId);
  if (greeted.includes(requestKey)) return;
  try {
    localStorage.setItem(
      storageKey(supplierId),
      JSON.stringify([...greeted, requestKey].slice(-MAX_REMEMBERED))
    );
  } catch {
    // Storage full or blocked — the greeting simply shows again next time.
  }
};
